import { Link } from 'react-router-dom';
import { StatusChip } from '@nyce/admin-kit/console';
import { Select } from '@nyce/admin-kit/ui';
import type { DbServerRef } from '../../api/db';
import { EmptyState, SortSelect, SortTh, TableScroller, useSort } from '../../components/common';
import { formatCount } from '../../lib/format';
import { serverName } from '../../pages/databases/shared';
import type { ScanDatabase } from '../api/indexAdvisor';
import { ReplicaOf, useParam } from '../shared';

const STATUS: Record<ScanDatabase['status'], { label: string; tone: 'green' | 'grey' | 'red' }> = {
  ok: { label: 'ok', tone: 'green' },
  no_advisor: { label: 'no advisor', tone: 'grey' },
  error: { label: 'error', tone: 'red' },
};

const adviceLink = (d: ScanDatabase) =>
  `/databases?tab=advice&server=${encodeURIComponent(d.serverId)}&db=${encodeURIComponent(d.database)}`;

/** Each tenant database the scan read: ok, no advisor, or the error it gave. */
export function ScanDatabasesTab({
  databases,
  servers,
}: {
  databases: ScanDatabase[];
  servers: DbServerRef[];
}) {
  const [status, setStatus] = useParam('status', '');
  const filtered = databases.filter((d) => !status || d.status === status);
  const sort = useSort<ScanDatabase>(
    filtered,
    {
      server: { label: 'Server', value: (d) => serverName(servers, d.serverId) },
      database: { label: 'Database', value: (d) => d.database },
      status: { label: 'Status', value: (d) => d.status },
      candidates: { label: 'Candidates', value: (d) => d.candidates, desc: true },
    },
    useParam('sort', '')
  );
  const count = (s: ScanDatabase['status']) => databases.filter((d) => d.status === s).length;
  const replicas = databases.filter((d) => d.replicaOf).length;
  return (
    <>
      <div className="iax-toolbar">
        <Select
          aria-label="Status"
          value={status}
          onChange={setStatus}
          options={[
            { value: '', label: `All (${databases.length})` },
            ...(Object.keys(STATUS) as ScanDatabase['status'][]).map((s) => ({
              value: s,
              label: `${STATUS[s].label} (${count(s)})`,
            })),
          ]}
        />
        <SortSelect sort={sort} />
      </div>
      {!!replicas && (
        <p className="nyk-ui-hint">
          {formatCount(replicas)} {replicas === 1 ? 'row is a read replica' : 'rows are read replicas'}: read
          for their own workload, counted as their primary&apos;s database.
        </p>
      )}
      {!databases.length && (
        <EmptyState card title="No databases read yet." hint="Only tenant databases are listed." />
      )}
      {!!databases.length && !sort.rows.length && <EmptyState card title="No database with this status." />}
      {!!sort.rows.length && (
        <div className="nyk-card">
          <TableScroller>
            <table
              className="nyk-ui-table nyk-cards"
              aria-label="Databases of the scan"
              data-testid="iax-scan-databases"
            >
              <thead>
                <tr>
                  <SortTh sort={sort} by="database" />
                  <SortTh sort={sort} by="server" />
                  <SortTh sort={sort} by="status" />
                  <SortTh sort={sort} by="candidates" className="nyk-ui-num" />
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {sort.rows.map((d) => (
                  <tr key={`${d.serverId}/${d.database}`} data-db={`${d.serverId}/${d.database}`}>
                    <td className="nyk-card-title nyk-wrap">
                      {d.database ? (
                        <Link to={adviceLink(d)}>{d.database}</Link>
                      ) : (
                        <span className="iax-muted">database list</span>
                      )}
                    </td>
                    <td data-label="Server" className="nyk-wrap">
                      {serverName(servers, d.serverId)}
                      <ReplicaOf servers={servers} primary={d.replicaOf} />
                    </td>
                    <td data-label="Status">
                      <StatusChip
                        value={d.status}
                        tone={STATUS[d.status].tone}
                        label={STATUS[d.status].label}
                      />
                    </td>
                    <td data-label="Candidates" className="nyk-ui-num">
                      {formatCount(d.candidates)}
                    </td>
                    <td data-label="Error" className="nyk-wrap">
                      {d.error ? <span className="nyk-text-red">{d.error}</span> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
    </>
  );
}
